module.exports = class ImageConverter {
  async webp(file, settings) {
    const img = await this.loadImage(file);
    const maxWidth = settings?.maxWidth || img.width;
    const maxHeight = settings?.maxHeight || img.height;
    const desiredAspectRatio = settings?.aspectRatio || img.width / img.height;
    const allowTransparency =
      settings?.allowTransparency !== undefined
        ? settings.allowTransparency
        : true;
    const backgroundColor = settings?.backgroundColor || "white";
    const widthRatio = maxWidth / img.width;
    const heightRatio = maxHeight / img.height;
    const ratio = Math.min(widthRatio, heightRatio, 1);
    const scaledWidth = img.width * ratio;
    const scaledHeight = img.height * ratio;
    let cropWidth = scaledWidth;
    let cropHeight = scaledHeight;
    const currentAspectRatio = scaledWidth / scaledHeight;
    if (currentAspectRatio > desiredAspectRatio)
      cropWidth = scaledHeight * desiredAspectRatio;
    else if (currentAspectRatio < desiredAspectRatio)
      cropHeight = scaledWidth / desiredAspectRatio;
    const offsetX = (scaledWidth - cropWidth) / 2;
    const offsetY = (scaledHeight - cropHeight) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext("2d");
    if (!allowTransparency) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(
      img,
      offsetX / ratio,
      offsetY / ratio,
      cropWidth / ratio,
      cropHeight / ratio,
      0,
      0,
      cropWidth,
      cropHeight
    );
    const blob = await this.canvasToBlob(canvas, "image/webp", 0.8);
    const webpFile = new File([blob], file.name.replace(/\.\w+$/, ".webp"), {
      type: "image/webp",
    });
    return webpFile;
  }

  /**
   * Reduz só a resolução (sem degradar qualidade) e exporta WebP/JPEG.
   * @returns {{ image: string, mimeType: string }}
   */
  async compressBase64(base64, mimeType = "image/jpeg", settings = {}) {
    const dataUrl = String(base64 || "").startsWith("data:")
      ? base64
      : `data:${mimeType};base64,${base64}`;

    const img = await this.loadImageFromUrl(dataUrl);
    const blob = await this.compressImageElement(img, settings);
    const compressedBase64 = await this.blobToBase64(blob);
    return {
      image: compressedBase64,
      mimeType: blob.type || settings.outputType || "image/webp",
    };
  }

  /**
   * Reduz só a resolução de um File/Blob e exporta WebP/JPEG.
   * @returns {Promise<File>}
   */
  async compressFile(file, settings = {}) {
    const img = await this.loadImage(file);
    const blob = await this.compressImageElement(img, settings);
    const outputType = blob.type || settings.outputType || "image/webp";
    const ext = outputType === "image/jpeg" ? "jpg" : "webp";
    const baseName = String(file?.name || "reference").replace(/\.\w+$/, "");
    return new File([blob], `${baseName}.${ext}`, { type: outputType });
  }

  async compressImageElement(img, settings = {}) {
    const outputType = settings.outputType || "image/webp";
    // Qualidade alta: evita artefatos que a IA propaga na imagem gerada
    const quality =
      typeof settings.quality === "number" ? settings.quality : 0.92;

    let maxWidth = settings.maxWidth;
    let maxHeight = settings.maxHeight;
    if (!maxWidth && !maxHeight) {
      const maxEdge = settings.maxEdge || 1280;
      maxWidth = maxEdge;
      maxHeight = maxEdge;
    } else {
      maxWidth = maxWidth || img.width;
      maxHeight = maxHeight || img.height;
    }

    const scale = Math.min(
      1,
      maxWidth / img.width,
      maxHeight / img.height
    );
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (outputType === "image/jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await this.canvasToBlob(canvas, outputType, quality);
    if (!blob) {
      throw new Error("Falha ao compactar imagem");
    }
    return blob;
  }

  loadImage(file) {
    return this.loadImageFromUrl(URL.createObjectURL(file), true);
  }

  loadImageFromUrl(src, revokeOnLoad = false) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = function () {
        if (revokeOnLoad) URL.revokeObjectURL(img.src);
        resolve(img);
      };
      img.onerror = function (error) {
        if (revokeOnLoad) URL.revokeObjectURL(img.src);
        reject(error);
      };
      img.src = src;
    });
  }

  canvasToBlob(canvas, type, quality) {
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        type,
        quality
      );
    });
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
};
